import { Gender } from '@/enums';
import {
  AddressDetail,
  BankAccountDetail,
  InfoState,
  InsuredDetail,
  PersonalDetail,
} from '@/types/InfoState';
import { HomeRetrieveProposalResponse } from '@/types/Proposal';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { format, parseISO } from 'date-fns';

const initialState: InfoState = {
  personalDetail: {
    name: '',
    formerName: '',
    aliasName: '',
    idType: 0,
    idNumber: '',
    idNoExpiryDate: '',
    secondaryIdType: 0,
    secondaryIdNumber: '',
    secondaryIdNoExpiryDate: '',
    alternateIdType: 0,
    alternateIdNo: '',
    alternateIdNoExpiryDate: '',
    dualNationality: undefined,
    dateOfBirth: '',
    gender: Gender.M,
    ethnicity: 0,
    mobileNo: '',
    email: '',
    bankId: '',
    bankName: '',
    bankAccountNumber: '',
    nationality: undefined,
  },
  insuredDetails: [],
  propertyDetail: {
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    postcode: '',
  },
  mailingDetail: {
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    postcode: '',
    propertySameAddressCheck: false,
  },
  bankAccountDetail: {
    bankId: 0,
    accountHolder: '',
    accountNo: '',
  },
  loading: false,
  error: false,
};

export const infoStateSlice = createSlice({
  name: 'info',
  initialState,
  reducers: {
    resetInfoState: () => {
      return initialState;
    },
    updateDateOfBirth: (state, action: PayloadAction<string>) => {
      state.personalDetail.dateOfBirth = action.payload;
    },
    updatePersonalDetail: (state, action: PayloadAction<PersonalDetail>) => {
      const data = action.payload;
      state.personalDetail = {
        name: data.name,
        formerName: data.formerName,
        aliasName: data.aliasName,
        idType: data.idType,
        idNumber: data.idNumber,
        idNoExpiryDate: data.idNoExpiryDate,
        secondaryIdType: data.secondaryIdType,
        secondaryIdNumber: data.secondaryIdNumber,
        secondaryIdNoExpiryDate: data.secondaryIdNoExpiryDate,
        alternateIdType: data.alternateIdType,
        alternateIdNo: data.alternateIdNo,
        alternateIdNoExpiryDate: data.alternateIdNoExpiryDate,
        dualNationality: data.dualNationality,
        dateOfBirth: data.dateOfBirth,
        gender: data.gender,
        ethnicity: data.ethnicity,
        mobileNo: data.mobileNo ? '0' + data.mobileNo : '',
        email: data.email,
        bankId: data.bankId,
        bankName: data.bankName,
        bankAccountNumber: data.bankAccountNumber,
        nationality: data.nationality,
      };

      state.bankAccountDetail.accountHolder = data.name;
    },
    updateInsuredDetails: (state, action: PayloadAction<InsuredDetail[]>) => {
      state.insuredDetails = action.payload;
    },
    updatePropertyAddressDetail: (
      state,
      action: PayloadAction<AddressDetail>,
    ) => {
      const data = action.payload;
      state.propertyDetail = {
        addressLine1: data.addressLine1,
        addressLine2: data.addressLine2,
        city: data.city,
        state: data.state,
        country: data.country,
        postcode: data.postcode,
      };
    },
    updateMailingAddressDetail: (
      state,
      action: PayloadAction<AddressDetail>,
    ) => {
      const data = action.payload;
      state.mailingDetail = {
        addressLine1: data.addressLine1,
        addressLine2: data.addressLine2,
        city: data.city,
        state: data.state,
        country: data.country,
        postcode: data.postcode,
        propertySameAddressCheck: data.propertySameAddressCheck,
      };
    },
    updateBankDetail: (state, action: PayloadAction<BankAccountDetail>) => {
      const data = action.payload;
      state.bankAccountDetail.bankId = data.bankId;
      state.bankAccountDetail.accountNo = data.accountNo;
    },
    mapProposalDataToInfoState: (
      state,
      action: PayloadAction<HomeRetrieveProposalResponse>,
    ) => {
      const data = action.payload;

      state.bankAccountDetail = {
        bankId: data.bankId,
        accountHolder: data.accountHolder,
        accountNo: data.accountNo,
      };

      state.mailingDetail = {
        addressLine1: data.policyOwner.address?.addressLine1 ?? '',
        addressLine2: data.policyOwner.address?.addressLine2 ?? '',
        postcode: data.policyOwner.address?.postcode ?? '',
        city: data.policyOwner.address?.city ?? '',
        state: data.policyOwner.address?.state ?? '',
        country: data.policyOwner.address?.country ?? '',
      };

      state.propertyDetail = {
        addressLine1: data.home.address.address1,
        addressLine2: data.home.address.address2,
        postcode: data.home.address.postcode,
        city: data.home.address.city,
        state: data.home.address.state,
        country: data.home.address.country,
      };

      state.personalDetail = {
        name: data.policyOwner.name,
        formerName: data.policyOwner.formerName,
        aliasName: data.policyOwner.aliasName,
        idType: Number(data.policyOwner.identityType),
        idNumber: data.policyOwner.identityNumber,
        idNoExpiryDate: data.policyOwner.idNoExpiryDate ?? '',
        secondaryIdType: Number(data.policyOwner.secondaryIdentityType),
        secondaryIdNumber: data.policyOwner.secondaryIdentityNumber ?? '',
        secondaryIdNoExpiryDate: data.policyOwner.secondaryIdNoExpiryDate ?? '',
        alternateIdType: data.policyOwner.alternateIdType,
        alternateIdNo: data.policyOwner.alternateIdNo ?? '',
        alternateIdNoExpiryDate: data.policyOwner.alternateIdNoExpiryDate ?? '',
        nationality: data.policyOwner.nationality,
        dualNationality: data.policyOwner.dualNationality,
        dateOfBirth: format(parseISO(data.policyOwner.birthDate), 'dd/MM/yyyy'),
        gender: data.policyOwner.gender as Gender,
        ethnicity: data.policyOwner.ethnicity,
        mobileNo: data.policyOwner.mobile ?? '',
        email: data.policyOwner.email ?? '',
      };

      state.insuredDetails = (data.insured ?? []).map((insured: any) => ({
        name: insured.name,
        formerName: insured.formerName ?? '',
        aliasName: insured.aliasName ?? '',
        idType: Number(insured.identityType),
        idNumber: insured.identityNumber,
        idNoExpiryDate: insured.idNoExpiryDate ?? '',
        secondaryIdType: insured.secondaryIdentityType
          ? Number(insured.secondaryIdentityType)
          : undefined,
        secondaryIdNumber: insured.secondaryIdentityNumber ?? '',
        secondaryIdNoExpiryDate: insured.secondaryIdNoExpiryDate ?? '',
        alternateIdType: insured.alternateIdType,
        alternateIdNo: insured.alternateIdNo ?? '',
        alternateIdNoExpiryDate: insured.alternateIdNoExpiryDate ?? '',
        nationality: insured.nationality,
        dualNationality: insured.dualNationality,
        dateOfBirth: insured.birthDate
          ? format(parseISO(insured.birthDate), 'dd/MM/yyyy')
          : '',
        gender: insured.gender as Gender,
        ethnicity: insured.ethnicity,
        address: insured.address
          ? {
              addressLine1: insured.address.addressLine1,
              addressLine2: insured.address.addressLine2,
              postcode: insured.address.postcode,
              city: insured.address.city,
              state: insured.address.state,
              country: insured.address.country,
            }
          : undefined,
      }));
    },
  },
});

export const {
  updateDateOfBirth,
  resetInfoState,
  updatePersonalDetail,
  updateInsuredDetails,
  updatePropertyAddressDetail,
  updateMailingAddressDetail,
  updateBankDetail,
  mapProposalDataToInfoState,
} = infoStateSlice.actions;

export default infoStateSlice.reducer;
